import { loadGoogleFont } from "utils/googleFonts";
import "resources/styles/template3.css";
import { Form, FormInstance } from "antd";
import { useEffect } from "react";
import useGetLogo from "../hooks/useGetLogo";
import { sanitizeHtml } from "utils/sanitizeHtml";

interface FontOption {
  value: unknown;
  label: unknown;
}

export default function Template3({
  form,
  newFontsOptions,
  companyData,
  companyBankAccount,
}: {
  form: FormInstance;
  newFontsOptions: FontOption[];
  companyData: {
    company: GetCompanyWithAddressCompany;
    address: AddressResult;
  };
  companyBankAccount: BankAccountResult;
}): React.ReactElement {
  const logoDescription = Form.useWatch(["logoDescription"], form);
  const tableBody = Form.useWatch(["tableBorder"], form);
  const tableHeaderColor = Form.useWatch(["baseTextColorBackground"], form);
  const watermarkOpacity = Form.useWatch(["backgroundImageOpacity"], form);
  const baseColor = Form.useWatch(["baseColor"], form);
  const baseTextColor = Form.useWatch(["baseTextColor"], form);
  const titleColor = Form.useWatch(["titleColor"], form);
  const font = Form.useWatch(["font"], form);
  const showName = Form.useWatch(["showName"], form);
  const showDescription = Form.useWatch(["showDescription"], form);
  const showStampSpace = Form.useWatch(["showStampSpace"], form);
  const showMentionAndLegalStatus = Form.useWatch(["showMentionAndLegalStatus"], form);
  const logo = Form.useWatch(["logo"], form);
  const hasLogo = logo?.file?.status !== "uploading";
  const note = Form.useWatch(["note"], form);
  const latePaymentPenalty = Form.useWatch(["latePaymentPenalty"], form);
  const fixedRecoveryFee = Form.useWatch(["fixedRecoveryFee"], form);
  const discount = Form.useWatch(["discount"], form);
  const backgroundImage = Form.useWatch(["backgroundImage"], form);
  const logoPosition = Form.useWatch(["logoPosition"], form);
  const showDeliveryAddress = Form.useWatch(["showDeliveryAddress"], form);
  const fontName = (
    newFontsOptions?.find((item: FontOption) => item?.value === font)?.label as
      | { props?: { children?: string } }
      | undefined
  )?.props?.children;

  const address = companyData?.address;
  const company = companyData?.company;
  const bank = companyBankAccount;
  const isIntreprenneur = company?.legalCategory?.code === "1000";
  const { generateByLogo, accountingFirmName } = useGetLogo();

  useEffect(() => {
    if (fontName) {
      loadGoogleFont(fontName);
    }
  }, [fontName]);

  const fontStyle = fontName ? { fontFamily: `"${fontName}", sans-serif` } : {};

  return (
    <div className={`A4Container3 logo-position3-${logoPosition || "left"}`} style={fontStyle}>
      {backgroundImage && (
        <div
          className="background-image3"
          style={{
            opacity: watermarkOpacity || 0.05,
          }}
        >
          <img src={backgroundImage} alt="" />
        </div>
      )}

      {/* Header */}
      <header className="header-section3" style={{ borderBottomColor: baseColor || "#e5e7eb" }}>
        <div className="logo-area3">
          {hasLogo ? (
            <img src={logo} alt="" />
          ) : (
            <div className="logo-placeholder" style={{ borderColor: baseColor || "#e0e0e0" }}>
              <span style={{ color: baseTextColor || "#000" }}>LOGO</span>
            </div>
          )}
          <div className="logo-description3" style={{ color: titleColor || "#000", ...fontStyle }}>
            {logoDescription}
          </div>
        </div>
        <div className="invoice-info3">
          <h1 className="invoice-title3" style={{ color: titleColor || "#000", ...fontStyle }}>
            FACTURE
          </h1>
          <p className="invoice-subtitle3" style={{ color: titleColor || "#000", ...fontStyle }}>
            <b>Facture n°:</b> F-123456789
          </p>
          <p className="invoice-subtitle3" style={{ color: titleColor || "#000", ...fontStyle }}>
            <b>Date:</b> {new Date().toLocaleDateString("fr-FR")}
          </p>
        </div>
      </header>

      {/* Three Columns */}
      <div className="three-columns3" style={{ color: baseTextColor || "#000" }}>
        <div className="column3">
          <div
            className="column-title3"
            style={{ borderBottomColor: baseColor || "#000", color: titleColor || "#000" }}
          >
            Émis par
          </div>
          <div className="column-content3">
            <address>
              <b>{company?.displayName || company?.name}</b>
              <span>{address?.address1 || ""}</span>
              <span>{address?.address2 || ""}</span>
              <span>
                {address?.postalCode || ""} {address?.city || ""} {address?.country?.name || ""}
              </span>
              <span>{company?.email || ""}</span>
              <span>{company?.phone1 || ""}</span>
            </address>
          </div>
        </div>

        <div className="column3">
          <div
            className="column-title3"
            style={{ borderBottomColor: baseColor || "#000", color: titleColor || "#000" }}
          >
            Facturé à
          </div>
          <div className="column-content3">
            <address>
              <b>ACME CORPORATION - ENTREPÔT</b>
              <span>789 Avenue de la Logistique</span>
              <span>Zone Industrielle</span>
              <span>75019 Paris, France</span>
              <span>contact@acme-corp.com</span>
              <span>+33 1 23 45 67 89</span>
            </address>
            {showDeliveryAddress && (
              <>
                <address>
                  <b style={{ color: titleColor || "#1e293b" }}>Adresse de livraison:</b>
                  <span>ACME CORPORATION - LIVRAISON</span>
                  <span>456 Rue de la Distribution</span>
                  <span>Quartier Commercial</span>
                  <span>75008 Paris, France</span>
                </address>
              </>
            )}
          </div>
        </div>
        <div className="column3">
          <div
            className="column-title3"
            style={{ borderBottomColor: baseColor || "#000", color: titleColor || "#000" }}
          >
            Informations
          </div>
          <div className="column-content3">
            {" "}
            <div className="info-row3">
              <span className="info-label3">Date:</span>
              <span className="info-value3">{new Date().toLocaleDateString("fr-FR")}</span>
            </div>
            <div className="info-row3">
              <span className="info-label3">TVA Intracom:</span>
              <span className="info-value3">{company?.euVatNumber || "—"}</span>
            </div>
            <div className="info-row3">
              <span className="info-label3">SIRET:</span>
              <span className="info-value3">{company?.vatNumber || "—"}</span>
            </div>
            <div className="info-row3">
              <span className="info-label3">CODE APE:</span>
              <span className="info-value3">{company?.businessActivity?.code || "—"}</span>
            </div>
            <div className="info-row3">
              <span className="info-label3">FORME JURIDIQUE:</span>
              <span className="info-value3">{company?.legalStructure?.code || "—"}</span>
            </div>
            <div className="info-row3">
              <span className="info-label3">RCS:</span>
              <span className="info-value3">{company?.rcs || "—"}</span>
            </div>
            <hr className="custom-divider" style={{ borderColor: baseColor || "#000" }} />
            <div className="info-row3">
              <span className="info-label3">N° Client:</span>
              <span className="info-value3">CLI-789123456</span>
            </div>
            <div className="info-row3">
              <span className="info-label3">TVA Intracom:</span>
              <span className="info-value3">FR12345678901</span>
            </div>
            <div className="info-row3">
              <span className="info-label3">SIRET:</span>
              <span className="info-value3">12345678901234</span>
            </div>
          </div>
        </div>
      </div>

      {/* Extra Info */}
      {(showName || showDescription) && (
        <div className={`extra-info3 ${showName && showDescription ? "compact" : ""}`}>
          {showName && (
            <div className="extra-name3" style={{ color: baseTextColor || "#000" }}>
              Refonte site web e-commerce
            </div>
          )}
          {showDescription && (
            <div className="extra-description3" style={{ color: baseTextColor || "#000" }}>
              Développement complet d&apos;une plateforme e-commerce avec système de paiement
              intégré et interface d&apos;administration avancée
            </div>
          )}
        </div>
      )}

      <div className={`default-table3 body-${tableBody || "border_none"}`}>
        <table
          style={{
            borderColor: baseColor || "#000",
          }}
        >
          <thead
            id="customColumnHeader"
            className="default-table__thead3"
            style={{
              backgroundColor: tableHeaderColor || "#ddd",
              color: titleColor || "#000",
              borderColor: baseColor || "#000",
            }}
          >
            <tr style={{ borderColor: baseColor || "#000" }}>
              <td
                colSpan={1}
                className="default-table__column-left3"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>REF</b>
              </td>
              <td
                colSpan={3}
                className="default-table__column-left3"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>DÉSIGNATION</b>
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>PU HT</b>
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>QTÉ</b>
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>UNITÉ</b>
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>MONTANT HT</b>
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>TVA %</b>
              </td>
            </tr>
          </thead>
          <tbody style={{ color: baseTextColor || "#000" }}>
            <tr className="row" style={{ borderColor: baseColor || "#000" }}>
              <td
                colSpan={1}
                className="line-description default-default__lines__column-left3"
                style={{ borderColor: baseColor || "#000" }}
              >
                <span>SERV-001</span>
              </td>
              <td
                colSpan={3}
                className="default-table__column-left3"
                style={{ borderColor: baseColor || "#000" }}
              >
                Consultation développement web
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                150.00
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                8
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                HEURE
              </td>
              <td
                id="td-vatPct"
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                1,200.00
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                20.00%
              </td>
            </tr>
            <tr className="row" style={{ borderColor: baseColor || "#000" }}>
              <td
                colSpan={1}
                className="line-description default-default__lines__column-left"
                style={{ borderColor: baseColor || "#000" }}
              >
                <span>PROD-002</span>
              </td>
              <td
                colSpan={3}
                className="default-table__column-left3"
                style={{ borderColor: baseColor || "#000" }}
              >
                Ordinateur portable professionnel
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                899.00
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                2
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                UNITAIRE
              </td>
              <td
                id="td-vatPct"
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                1,798.00
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                20.00%
              </td>
            </tr>
            <tr className="row" style={{ borderColor: baseColor || "#000" }}>
              <td
                colSpan={1}
                className="line-description default-default__lines__column-left3"
                style={{ borderColor: baseColor || "#000" }}
              >
                <span>LIC-003</span>
              </td>
              <td
                colSpan={3}
                className="default-table__column-left3"
                style={{ borderColor: baseColor || "#000" }}
              >
                Licence logiciel annuelle
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                450.00
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                1
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                UNITAIRE
              </td>
              <td
                id="td-vatPct"
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                450.00
              </td>
              <td
                colSpan={1}
                className="default-table__column-right3"
                style={{ borderColor: baseColor || "#000" }}
              >
                20.00%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Totals and Payment Section Combined */}
      <br />
      <div className="totals-payment-combined3">
        <div className="payment-section3" style={{ marginBottom: 0, flex: 1 }}>
          <h3
            className="payment-title3"
            style={{ borderBottomColor: baseColor || "#000", color: titleColor || "#000" }}
          >
            Informations de paiement
          </h3>
          <div className="payment-grid3" style={{ color: baseTextColor || "#000" }}>
            <div className="payment-column3">
              <div className="payment-item3">
                <span className="payment-label3">Mode de paiement:</span>
                <span className="payment-value3">Virement bancaire</span>
              </div>
              <div className="payment-item3">
                <span className="payment-label3">Conditions:</span>
                <span className="payment-value3">30 jours net</span>
              </div>
              <div className="payment-item3">
                <span className="payment-label3">Banque:</span>
                <span className="payment-value3">{bank?.name || "Crédit Agricole"}</span>
              </div>
              <div className="payment-item3">
                <span className="payment-label3">BIC/SWIFT:</span>
                <span className="payment-value3">{bank?.bic || "AGRIFRPP874"}</span>
              </div>
              <div className="payment-item3">
                <span className="payment-label3">IBAN:</span>
                <span className="payment-value3" style={{ fontSize: "7pt" }}>
                  {bank?.iban || "FR76 1254 8029 8765 4321 0987 654"}
                </span>
              </div>
              <div className="payment-item3">
                <span className="payment-label3">Référence:</span>
                <span className="payment-value3">F-123456789</span>
              </div>
            </div>
          </div>
        </div>
        <div className="totals-container3">
          <div
            className="totals-box3"
            style={{ borderColor: baseColor || "#000", color: baseTextColor || "#000" }}
          >
            <div
              className="totals-row3"
              style={{ borderBottomColor: baseColor ? `${baseColor}40` : "#00000040" }}
            >
              <span className="totals-label3">Total HT</span>
              <span className="totals-value3">3,448.00 €</span>
            </div>
            <div
              className="totals-row3"
              style={{ borderBottomColor: baseColor ? `${baseColor}40` : "#00000040" }}
            >
              <span className="totals-label3">Total HT NET</span>
              <span className="totals-value3">3,448.00 €</span>
            </div>
            <div
              className="totals-row3"
              style={{ borderBottomColor: baseColor ? `${baseColor}40` : "#00000040" }}
            >
              <span className="totals-label3">TVA 20%</span>
              <span className="totals-value3">689.60 €</span>
            </div>
            <div className="totals-row3" style={{ borderTopColor: baseColor || "#000" }}>
              <span className="totals-label3">Total TTC</span>
              <span className="totals-value3">4,137.60 €</span>
            </div>
          </div>
        </div>
      </div>
      <hr className="custom-divider" style={{ borderColor: baseColor || "#000" }} />
      <div
        className="footer-content3"
        style={{ borderTopColor: baseColor || "#e5e7eb", color: baseTextColor || "#000" }}
      >
        <div className="terms-section3">
          {showMentionAndLegalStatus && (
            <div
              className="legal-notice3"
              style={{ color: baseTextColor || "#666", borderLeftColor: baseColor || "#000" }}
            >
              Article L441-6 du code du commerce, tout retard de paiement donnera lieu à
              l&apos;application d&apos;une pénalité égale à trois fois le taux d&apos;intérêt
              légal. Une indemnité forfaitaire pour frais de recouvrement de 40 € sera appliquée.
            </div>
          )}
          {note && (
            <div className="note-section3" style={{ color: baseTextColor || "#333" }}>
              <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(note) }} />
            </div>
          )}

          <div className="extra-terms-container3">
            {latePaymentPenalty && (
              <div className="extra-terms3">
                <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(latePaymentPenalty || "") }} />
              </div>
            )}
            {fixedRecoveryFee && (
              <div className="extra-terms3">
                <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(fixedRecoveryFee || "") }} />
              </div>
            )}
            {discount && (
              <div className="extra-terms3">
                <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(discount || "") }} />
              </div>
            )}
            {isIntreprenneur && (
              <div className="extra-terms3">
                <span>TVA non applicable, article 293 B du CGI.</span>
              </div>
            )}
          </div>
        </div>
        {showStampSpace && (
          <div className="stamp-area3" style={{ borderColor: baseColor || "#000" }}>
            <span className="stamp-text3">Cachet et signature</span>
          </div>
        )}
      </div>

      {/* Absolute Footer */}
      <footer
        className="absolute-footer3"
        style={{ borderTopColor: baseColor || "#000", color: baseTextColor || "#000" }}
      >
        <div className="footer-branding3" style={{ borderBottomColor: baseColor || "#000" }}>
          <span>Document généré par</span>
          <strong>NeoFact</strong>
          <span>by</span>
          <img
            src={generateByLogo ?? undefined}
            alt={accountingFirmName ?? undefined}
            className="newrai-logo3"
          />
        </div>
        <div className="company-footer3">
          <div className="company-name-footer3" style={{ color: baseTextColor || "#000" }}>
            {company?.displayName || company?.name}
          </div>
          <div>
            {address?.address1}, {address?.postalCode} {address?.city} • TVA: {company?.euVatNumber}{" "}
            • SIRET: {company?.vatNumber} •{company?.email} • {company?.phone1}
          </div>
        </div>
      </footer>
    </div>
  );
}
